import {IActionDispatcher, IPaylodAction} from '../../interfaces/dispatchers'
import {FieldDescription, ISourceDescription, ISourceDescriptions} from '../../interfaces/models'

import {DeniedServiceInfo} from '../../models/DeniedServiceInfo'
import {FailedServiceInfo} from '../../models/FailedServiceInfo'

import axios from 'axios'

import {IFeatureLayerInfo, IPropertyTypes} from '../../interfaces/models'
import {IStore} from '../../interfaces/reducers'

import {LayerInfosService} from '..'

import {Extent} from 'ol/extent'
import TileState from 'ol/TileState'
import {v4 as uuidv4} from 'uuid'
import {GeometryType} from '../../enums/GeometryType'
import {
  Feature,
  FeatureLayerInfo,
  GeoJSON,
  ImageLayer,
  ImageWMS,
  Projection,
  TileLayer,
  TileWMS,
  XYZ
} from '../../models'

export class WFSLayerInfosService extends LayerInfosService {
  public propToCommonType(wfsPropType: string): IPropertyTypes | null {
    switch (wfsPropType) {
      case 'xsd:string':
        return 'string'
      case 'xsd:int':
        return 'integer'
      case 'xsd:date-time':
        return 'date'
      case 'gml:MultiPolygon':
      case 'gml:MultiLineString':
      case 'gml:MultiPoint':
      case 'gml:Polygon':
      case 'gml:LineString':
      case 'gml:Point':
        return 'geometry'
      case 'xsd:date':
        return 'date'
      case 'xsd:number':
        return 'double'
      case 'xsd:string':
        return 'string'
      case 'xsd:string':
        return 'string'
      default:
        console.error(`${wfsPropType} not implemented yet`)
        return null
    }
  }

  public propToGeometryType(wfsPropType: string): GeometryType {
    switch (wfsPropType) {
      case 'gml:MultiPolygon':
        return GeometryType.MultiPolygon
      case 'gml:MultiLineString':
        return GeometryType.MultiLineString
      case 'gml:MultiPoint':
        return GeometryType.MultiPoint
      case 'gml:Polygon':
        return GeometryType.Polygon
      case 'gml:LineString':
        return GeometryType.LineString
      case 'gml:Point':
        return GeometryType.Point

      default:
        return GeometryType.None
    }
  }

  public getVectorLoadFunction(
    layerInfo: IFeatureLayerInfo,
    spCore: string,
    getStore: () => IStore,
    dispatch: (data: IPaylodAction | IActionDispatcher) => void
  ): (extent: Extent, resolution: number, projection: Projection) => Promise<Array<Feature>> {
    return (extent: Extent, resolution: number, projection: Projection) => {
      return new Promise((resolve, reject) => {
        const url =
          `${layerInfo.sourceDescription.namespacedUrl}/wfs?typename=${layerInfo.sourceDescription.layerName}` +
          `&service=WFS&version=1.1.0&request=GetFeature&outputFormat=application/json&srsname=${spCore}&bbox=${extent.join(
            ','
          )},${spCore}`

        axios
          .get(url)
          .then(response => {
            const features = new GeoJSON().readFeatures(response.data, {
              featureProjection: projection
            } as any) as any as Feature[]
            console.warn('Not sure if the parsing is correct here...MultiPoint and Point etc parsing...')
            resolve(features)
          })
          .catch(error => {
            console.error(error)
            resolve([])
          })
      })
    }
  }

  protected descriptionFromLocalDescriptions(source: ISourceDescription): FieldDescription[] {
    const result: FieldDescription[] = []
    for (const key in source.fieldInfos) {
      const field = source.fieldInfos[key]
      result.push({
        name: key,
        type: field.type || 'string',
        nullable: !!field.nullable,
        length: field.length || 256,
        codedValues: field.codedValues,
        geometryType: GeometryType.Polygon
      })
    }
    return result
  }

  protected infosFromLocalDescriptions(sources: ISourceDescriptions): IFeatureLayerInfo[] {
    const result: FeatureLayerInfo[] = []
    for (const key in sources) {
      result.push(
        new FeatureLayerInfo(
          this.getFieldsInfo(this.descriptionFromLocalDescriptions(sources[key]), sources[key]),
          sources[key]
        )
      )
    }
    return result
  }

  public fetchFeaturesDescriptions(namespaceGroupedSourceDescriptions: {
    [namespaceUrl: string]: ISourceDescriptions
  }): Promise<Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo>> {
    const namespaceInfoPromises: Array<Promise<Array<IFeatureLayerInfo | FailedServiceInfo | DeniedServiceInfo>>> = []
    for (const namespacedUrl in namespaceGroupedSourceDescriptions) {
      let useLocalData = false
      for (const key in namespaceGroupedSourceDescriptions[namespacedUrl]) {
        if (namespaceGroupedSourceDescriptions[namespacedUrl][key].useLocalDescriptions) {
          useLocalData = true
          break
        }
      }
      if (useLocalData) {
        namespaceInfoPromises.push(
          new Promise((resolve, reject) => {
            resolve(this.infosFromLocalDescriptions(namespaceGroupedSourceDescriptions[namespacedUrl]))
          })
        )
      } else {
        namespaceInfoPromises.push(
          new Promise((resolve, reject) => {
            axios
              .get(
                `${namespacedUrl}/wfs?version=1.1.0&request=describeFeatureType&outputFormat=application/json&service=WFS`
              )
              .then(response => {
                const desc = response.data.featureTypes as Array<WfsFeatureDescription>
                const layerToFieldDescriptions: {[layerUrl: string]: Array<FieldDescription>} = {}
                desc.forEach(f => {
                  const layerUrl = `${namespacedUrl}/${f.typeName}`
                  layerToFieldDescriptions[layerUrl] = []
                  f.properties.forEach(p => {
                    const type = this.propToCommonType(p.type)
                    if (type) {
                      layerToFieldDescriptions[layerUrl].push({
                        name: p.name,
                        type: type,
                        nullable: p.nillable,
                        length: 255, // TODO,
                        geometryType: this.propToGeometryType(p.type)
                      })
                    }
                  })
                })
                resolve(
                  this.createFeatureLayerInfo(
                    layerToFieldDescriptions,
                    namespaceGroupedSourceDescriptions[namespacedUrl]
                  )
                )
              })
              .catch((error: Error) => {
                //TODO - remove hard coded 401
                resolve(this.createFailedLayerInfo(error, 401, namespaceGroupedSourceDescriptions[namespacedUrl]))
              })
          })
        )
      }
    }
    return this.resolveNamespacePromises(namespaceInfoPromises)
  }

  public createImageLayer(
    layerInfos: IFeatureLayerInfo[],
    minRes: number,
    maxRes: number,
    type: 'tile' | 'image' | 'xyz',
    getStore: () => IStore
  ): TileLayer | ImageLayer {
    if (layerInfos.length == 0) {
      throw new Error(`cannot create wms layer with empty layer array`)
    }
    const z = layerInfos.length > 0 ? layerInfos[0].sourceDescription.z || 1 : 1
    const cqlFilters: string[] = []
    let namespaceUrl: string = ''
    layerInfos.forEach(l => {
      if (!namespaceUrl) {
        namespaceUrl = l.sourceDescription.namespacedUrl
      } else if (namespaceUrl != l.sourceDescription.namespacedUrl) {
        alert('Multiple layers with different namespaceUrl')
        throw Error('Multiple layers with different namespaceUrl')
      }
      cqlFilters.push(l.sourceDescription.constantSqlWhere)
    })
    const emptyLayers =
      layerInfos
        .filter(s => !!s.sourceDescription.visible)
        .map(s => s.sourceDescription.layerName)
        .join('').length == 0

    // The tileLoadFunction below sets tile.getImage().src to a blob: URL it
    // built itself via axios. OL's built-in image loader is never used here,
    // and blob: URLs are always same-origin — so `crossOrigin: 'anonymous'`
    // never applied. Dropped for clarity.
    const params = {
      url: namespaceUrl + '/wms',
      params: {
        LAYERS: layerInfos
          .filter(s => !!s.sourceDescription.visible)
          .map(s => s.sourceDescription.layerName)
          .join(','),
        TILED: true
      },
      serverType: 'geoserver'
    } as any
    if (cqlFilters.find(f => f != '1=1')) {
      params.params.CQL_FILTER = cqlFilters.join(';')
    }
    let resultLayer: TileLayer | ImageLayer
    if (type == 'tile') {
      const source = new TileWMS(params)
      source.setTileLoadFunction(
        this.getTileLoadFunction(
          namespaceUrl,
          layerInfos.sort(l => l.sourceDescription.z).map(l => l.sourceDescription.layerName),
          getStore
        )
      )
      resultLayer = new TileLayer({
        source: source,
        visible: true
      })
    } else if (type == 'image') {
      const source = new ImageWMS(params)
      source.setImageLoadFunction(
        this.getTileLoadFunction(
          namespaceUrl,
          layerInfos.sort(l => l.sourceDescription.z).map(l => l.sourceDescription.layerName),
          getStore
        )
      )
      resultLayer = new ImageLayer({
        source: source,
        visible: true
      })
    } else {
      const source = new XYZ({
        url: params.url + 'tile/{z}/{y}/{x}'
      })
      // source.setImageLoadFunction(this.getTileLoadFunction(namespaceUrl, layerNames, getStore))
      resultLayer = new TileLayer({
        source: source,
        visible: true
      })
    }
    resultLayer.setZIndex(z)
    resultLayer.setMaxResolution(maxRes)
    resultLayer.setMinResolution(minRes)
    resultLayer.moreInfo = {
      urls: layerInfos.map(n => n.sourceDescription.id),
      guid: uuidv4(),
      type: 'regular',
      refreshOnExtentChange: !!layerInfos.find(s => s.sourceDescription.refreshOnExtentChange == true),
      isActive: true
    }

    return resultLayer
  }

  protected getTileLoadFunction(namespaceUrl: string, layerNames: Array<string>, getStore: () => IStore) {
    return (tile: any, src: string) => {
      const url = new URL(src)
      const layers = url.searchParams.get('LAYERS')
      if (!layers) {
        tile.getImage().src = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkAAIAAAoAAv/lxKUAAAAASUVORK5CYII=`
        return
      }
      // Tie the axios request to the tile's lifecycle: OL calls tile.dispose()
      // when it cancels or evicts a tile (e.g. user panned away before the
      // tile finished loading). We monkey-patch dispose to abort the in-flight
      // fetch — without this, every cancelled tile keeps downloading until
      // completion and wastes both bandwidth and CPU on a tile that's
      // already off-screen.
      const ctrl = new AbortController()
      const originalDispose = tile.dispose?.bind(tile)
      tile.dispose = () => {
        ctrl.abort()
        if (originalDispose) originalDispose()
      }

      // Blob URL instead of base64 data URL: avoids the O(n²) String.fromCharCode
      // reduce that builds a string byte-by-byte, and saves the ~33% size
      // overhead of base64 encoding. revokeObjectURL on first onload/onerror
      // keeps the blob from leaking once the browser has decoded the PNG.
      axios
        .get(src, {responseType: 'blob', signal: ctrl.signal})
        .then(response => {
          const blobUrl = URL.createObjectURL(response.data)
          const img = tile.getImage() as HTMLImageElement
          const release = () => {
            URL.revokeObjectURL(blobUrl)
            img.removeEventListener('load', release)
            img.removeEventListener('error', release)
          }
          img.addEventListener('load', release)
          img.addEventListener('error', release)
          img.src = blobUrl
        })
        .catch(err => {
          // axios.isCancel(err) is true when the abort signal fired — the
          // tile was discarded by OL so we don't need to set an error state.
          if (axios.isCancel?.(err)) return
          tile.setState(TileState.ERROR)
        })
    }
  }
}

interface WfsFeatureDescription {
  typeName: string
  properties: Array<{
    name: string
    maxOccurs: number
    minOccurs: number
    nillable: boolean
    type: any //TODO
    localType: 'int' | 'MultiPolygon' | 'string'
  }>
}
